/**
 * Lo que Rechazadas y Trámites para Reingreso muestran una sola vez por
 * trámite: el motivo del rechazo, su comprobante y la vista de detalles. Un
 * consolidado se rechaza entero: el motivo se escribe una vez y se guarda en
 * todas sus facturas, el comprobante se adjunta al grupo y el ojo muestra
 * todas sus facturas juntas. El cliente no quiere nada de eso repetido en
 * cada factura.
 *
 * También el botón Reingresar, que ambas pantallas llevan en la banda del
 * trámite: devuelve el trámite a Trámites para Procesar sin borrar su
 * bitácora (motivo, comprobantes, control anterior y eventos).
 *
 * Usa de la pantalla: `pageIds`, `reason` ({ show, data, key }),
 * `detail` ({ show, data, items, code, key }), `loading`, `notify()`,
 * `asyncPagination()` y los helpers de TramiteBand.
 */
export default {
  data() {
    return {
      // Comprobantes de rechazo de la página, para el contador del botón.
      docs: [],
    }
  },
  methods: {
    // Las facturas de un consolidado guardan el mismo texto. Si alguna vez
    // difieren, se juntan para no perder ninguno al editarlo.
    motivoDe(group) {
      const textos = ((group && group.invoices) || [])
        .map(inv => (inv.observationsDenied || '').trim())
        .filter(Boolean)
      return Array.from(new Set(textos)).join(' / ')
    },
    loadDocCounts() {
      if (!this.pageIds.length) {
        this.docs = []
        return
      }
      this.$store
        .dispatch('invoices/getDocuments', {
          ids: this.pageIds.join(','),
          step: 'DENIED',
        })
        .then(docs => {
          this.docs = docs || []
        })
    },
    // Uno subido a un consolidado se guarda una vez por factura con el mismo
    // archivo: se cuenta una sola vez.
    docCount(group) {
      const ids = group.invoices.map(i => i.id)
      const files = this.docs
        .filter(d => ids.includes(d.invoiceId))
        .map(d => d.filePath)
      return new Set(files).size
    },
    // ===== Reingreso =====
    // Un consolidado se rechazó entero y reingresa entero: vuelve de una vez a
    // Trámites para Procesar. El backend avisa al cliente por correo con los
    // soportes del rechazo (sendEmails).
    reinit(group) {
      const ids = group.invoices.map(i => i.id)
      const message = this.esConsolidado(group)
        ? `El consolidado ${this.consecutivoDe(group)} vuelve a Trámites ` +
          `para Procesar con sus ${ids.length} facturas; se avisó al cliente.`
        : `El trámite ${group.invoices[0].invoiceNumber} vuelve a Trámites ` +
          'para Procesar; se avisó al cliente.'
      this.loading = true
      return this.$store
        .dispatch('transit/reinitTransit', { invoices: ids, sendEmails: true })
        .then(response => {
          // El API no lanza: devuelve la respuesta del error.
          if (!response || (response.status && response.status >= 400)) {
            throw new Error('reinit')
          }
          return this.asyncPagination()
        })
        .then(() => {
          this.notify(message, 'success')
        })
        .catch(() => {
          this.loading = false
          this.notify('No se pudo reingresar el trámite.', 'error')
        })
    },
    reingresarLabel(group) {
      return this.esConsolidado(group)
        ? 'Reingresar el consolidado completo'
        : 'Reingresar este trámite'
    },
    reingresarHint(group) {
      return this.esConsolidado(group)
        ? `Devuelve las ${group.invoices.length} facturas del consolidado ` +
            `${this.consecutivoDe(group)} a Trámites para Procesar y avisa ` +
            'al cliente por correo'
        : `Devuelve la factura ${group.invoices[0].invoiceNumber} a ` +
            'Trámites para Procesar y avisa al cliente por correo'
    },
    saveReason(group, value) {
      const clean = value == null ? '' : value.trim()
      if (this.motivoDe(group) === clean) return
      const previous = group.invoices.map(i => i.observationsDenied)
      const restore = () =>
        group.invoices.forEach((inv, i) => {
          inv.observationsDenied = previous[i]
        })
      group.invoices.forEach(inv => {
        inv.observationsDenied = clean
      })
      this.$store
        .dispatch('transit/deniedReason', {
          invoiceId: group.invoices.map(i => i.id),
          observations: clean,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            restore()
            this.notify('No se pudo guardar el motivo.', 'error')
          }
        })
        .catch(() => {
          restore()
          this.notify('No se pudo guardar el motivo.', 'error')
        })
    },
    openReasonFiles(group) {
      const isGroup = this.esConsolidado(group)
      this.reason = {
        data: {
          invoiceId: group.invoices.map(i => i.id),
          invoiceNumber: isGroup ? null : group.invoices[0].invoiceNumber,
          code: isGroup ? this.consecutivoDe(group) : null,
          observations: this.motivoDe(group),
        },
        show: true,
        key: this.reason.key + 1,
      }
    },
    // Un solo botón por trámite: en un consolidado muestra todas sus
    // facturas en el mismo diálogo.
    handleDetails(group) {
      this.loading = true
      Promise.all(
        group.invoices.map(row =>
          this.$store
            .dispatch('invoices/getItems', { invoiceId: row.id })
            .then(items => ({
              // El listado agrupado trae laboratorio/distribuidor como
              // texto, pero DialogDetails espera lab_invoices/dist_invoices.
              ...row,
              lab_invoices: { id: row.labId, name: row.laboratorio },
              dist_invoices: { id: row.distId, name: row.distribuidor },
              products: items ? items : null,
            }))
        )
      ).then(invoices => {
        this.loading = false
        const isGroup = this.esConsolidado(group)
        this.detail = {
          data: isGroup ? null : invoices[0],
          items: isGroup ? invoices : [],
          code: isGroup ? this.consecutivoDe(group) : '',
          show: true,
          key: this.detail.key + 1,
        }
      })
    },
    detallesHint(group) {
      return this.esConsolidado(group)
        ? `Ver los productos y datos de las ${group.invoices.length} facturas`
        : 'Ver los productos y datos de la factura'
    },
  },
}
